import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { addDaysISO, formatTime, humanISODay, todayISO, weekdayRu, weekdayOfISO } from '../../lib/time';
import { plural, pluralWithCount } from '../../lib/plural';
import { Notice } from '../../ui/parts';
import { useBooking, useBookingGuard } from '../BookingContext';
import { useCreateAppointment, useRescheduleAppointment, useSlots } from '../api';

const WEEK = 7;

/**
 * Шаг 3 из 5: сетка свободного времени. Сетку считает сервер — он знает про буфер
 * между приёмами, занятость ребёнка у других специалистов, дорогу, горизонт
 * записи и паузу. Здесь только рисуем то, что дали.
 */
export function Calendar() {
  const me = useMeContext();
  const navigate = useNavigate();
  const toast = useToast();
  const { patch } = useBooking();
  const booking = useBookingGuard((b) => b.specialistId !== null && b.serviceId !== null);

  const tz = me.center.tz;
  const tf = me.center.timeFormat;
  const [weeks, setWeeks] = useState(2);
  const [day, setDay] = useState<string | null>(null);

  const from = todayISO(tz);
  const to = addDaysISO(from, weeks * WEEK - 1);
  const slots = useSlots({
    specialistId: booking?.specialistId ?? null,
    serviceId: booking?.serviceId ?? null,
    childId: booking?.childId ?? null,
    from,
    to,
  });

  const days = slots.data ?? [];
  const selected = days.find((d) => d.date === day) ?? null;

  const create = useCreateAppointment();
  const move = useRescheduleAppointment();
  const busy = create.isPending || move.isPending;
  const moving = booking?.flow === 'move';

  // Пока грузится сетка, выбранный слот может исчезнуть — не даём отправить призрак.
  useEffect(() => {
    if (day && !days.some((d) => d.date === day)) setDay(null);
  }, [days, day]);

  useScreen(moving ? 'Перенос записи' : 'Дата и время', {
    label: busy ? 'Секунду…' : moving ? 'Перенести на это время' : 'Далее',
    disabled: busy || !booking?.startsAt,
    onClick: () => submit(),
  });

  if (!booking) return null;

  function submit() {
    if (!booking || !booking.startsAt) return;

    if (moving && booking.movingId) {
      move.mutate(
        { id: booking.movingId, startsAt: booking.startsAt },
        {
          onSuccess: (appointment) => {
            patch({ appointmentId: appointment.id, holdExpiresAt: appointment.hold_expires_at });
            navigate('/done');
          },
          onError: (e) => handleSlotError(e),
        },
      );
      return;
    }

    create.mutate(
      {
        specialistId: booking.specialistId!,
        serviceId: booking.serviceId!,
        childId: booking.childId!,
        startsAt: booking.startsAt,
      },
      {
        onSuccess: (res) => {
          patch({ appointmentId: res.appointment.id, holdExpiresAt: res.holdExpiresAt });
          // Первичный приём: анкету уже заполнили на первом входе, второй раз не спрашиваем.
          navigate(booking.flow === 'intake' ? '/confirm' : '/form');
        },
        onError: (e) => handleSlotError(e),
      },
    );
  }

  /**
   * «Слот заняли» и «ребёнок занят» — разные истории для родителя, и путать их
   * нельзя: в первом случае надо выбрать другое время у этого же специалиста,
   * во втором — понять, что ребёнок в это время у другого.
   */
  function handleSlotError(e: unknown) {
    if (!(e instanceof ApiError)) {
      toast('Не получилось — попробуйте ещё раз');
      return;
    }
    if (e.is('CHILD_BUSY')) {
      const other = e.details as { starts_at_utc?: number; display_name?: string } | undefined;
      const where = [other?.display_name, other?.starts_at_utc ? formatTime(other.starts_at_utc, tz, tf) : null]
        .filter(Boolean).join(', ');
      toast(where
        ? `Ребёнок уже занят у другого специалиста: ${where}`
        : 'Ребёнок в это время уже занят у другого специалиста');
      patch({ startsAt: null });
      return;
    }
    if (e.is('SLOT_TAKEN', 'SLOT_UNAVAILABLE', 'SLOT_NOT_IN_GRID')) {
      toast('Это время только что заняли — выберите другое');
      patch({ startsAt: null });
      void slots.refetch();
      return;
    }
    if (e.is('SUBSCRIPTION_READONLY')) {
      toast('Запись временно приостановлена');
      return;
    }
    toast(e.message);
  }

  return (
    <Screen>
      <div>
        <p className="eyebrow">{moving ? 'Перенос' : 'Шаг 3 из 5'}</p>
        <p className="lead" style={{ marginTop: 6 }}>
          {moving
            ? 'Выберите новое время. Старый слот освободится, оплата перенесётся.'
            : `Свободные окна · ${booking.specialistName}, на ${pluralWithCount(weeks * WEEK, 'день', 'дня', 'дней')} вперёд.`}
        </p>
      </div>

      <div className="month">
        <strong>{monthTitle(days[0]?.date ?? from)}</strong>
        <button type="button" onClick={() => setWeeks((w) => w + 2)}>Ещё две недели →</button>
      </div>

      <div className="days">
        {days.map((d) => (
          <button
            key={d.date}
            type="button"
            className="day"
            aria-pressed={day === d.date}
            onClick={() => {
              setDay(d.date);
              patch({ startsAt: null });
            }}
          >
            <small>{weekdayRu(weekdayOfISO(d.date))}</small>
            <b>{Number(d.date.slice(8))}</b>
            <span className="free">{d.slots.length} {plural(d.slots.length, 'окно', 'окна', 'окон')}</span>
          </button>
        ))}
      </div>

      {slots.isPending ? <p className="lead">Смотрим свободное время…</p> : null}

      {!slots.isPending && days.length === 0 ? (
        <Notice kind="calm">
          Свободных окон в этом промежутке нет. Загляните на две недели дальше или напишите администратору.
        </Notice>
      ) : null}

      <p className="eyebrow">{selected ? `${humanISODay(selected.date, tz)} · время` : 'Время'}</p>
      <div className="slots">
        {selected ? (
          selected.slots.map((s) => (
            <button
              key={s.startsAt}
              type="button"
              className="slot"
              aria-pressed={booking.startsAt === s.startsAt}
              onClick={() => patch({ startsAt: s.startsAt })}
            >
              {s.time}
            </button>
          ))
        ) : (
          <p className="lead" style={{ gridColumn: '1/-1' }}>Выберите день, чтобы увидеть свободное время.</p>
        )}
      </div>

      {moving ? null : (
        <Notice kind="calm">Слот держится за вами 15 минут, пока вы заполняете анкету.</Notice>
      )}
    </Screen>
  );
}

/** «Сентябрь 2026» — заголовок над лентой дней. */
function monthTitle(dateISO: string): string {
  const [y, m, d] = dateISO.split('-').map(Number);
  const name = new Intl.DateTimeFormat('ru-RU', { timeZone: 'UTC', month: 'long', year: 'numeric' })
    .format(Date.UTC(y, m - 1, d, 12));
  return name.slice(0, 1).toUpperCase() + name.slice(1).replace(' г.', '');
}
